import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList
} from '@components/ui/command';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@radix-ui/react-popover';
import { Separator } from '@radix-ui/react-separator';
import {
  type TraceWidth,
  WidthList,
  WidthToInfoMap
} from '@stratumn/studio-shared';
import { Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

type Props = {
  selectedWidth: TraceWidth;
  onWidthChange: (width: TraceWidth) => void;
  /** Prevents the input id from collision. */
  id?: string;
  label?: string;
};
export const TraceWidthSelector = ({
  selectedWidth,
  onWidthChange,
  label = 'Column width',
  id
}: Props) => {
  const [open, setOpen] = useState(false);

  const onCustomWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const customWidth = Number(rawValue);
    if (rawValue === '' || Number.isNaN(customWidth) || customWidth < 0) {
      onWidthChange('medium');
      return;
    }
    onWidthChange(customWidth);
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <div className="grid w-full max-w-sm cursor-pointer select-none items-center gap-1.5">
          <Label>{label}</Label>
          <div className="hover:bg flex h-8 w-[220px] items-center justify-between rounded-md border border-input border-solid bg-background px-3 py-2 font-normal text-sm ring-offset-background hover:bg-accent hover:text-accent-foreground">
            {typeof selectedWidth === 'string'
              ? (WidthToInfoMap[selectedWidth]?.name ?? selectedWidth)
              : `${selectedWidth.toString()}px`}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="z-50 w-[220px] p-0">
        <Command loop>
          <CommandList>
            <CommandGroup heading="Predefined widths">
              {WidthList.map(widthItem => {
                const { name: widthName, pixel: widthPixel } =
                  WidthToInfoMap[widthItem];
                return (
                  <CommandItem
                    key={widthItem}
                    onSelect={currentValue => {
                      onWidthChange(currentValue as TraceWidth);
                      setOpen(false);
                    }}
                    value={widthItem}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedWidth === widthItem
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <div className="flex items-center gap-3">
                      {widthName}
                      <div className="rounded-sm border border-gray-300 border-solid bg-gray-100 px-2 text-xs">
                        {widthPixel}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <Separator />
            <CommandGroup heading="Custom width (px)">
              <CommandItem className="pointer-events-none">
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Input
                    autoFocus
                    className="pointer-events-auto w-full"
                    id={id ? `custom-width-${id}` : 'custom-width'}
                    min={0}
                    onChange={onCustomWidthChange}
                    placeholder="Ex: 210"
                    step={1}
                    type="number"
                    value={
                      typeof selectedWidth === 'number' ? selectedWidth : ''
                    }
                  />
                </div>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
